import React, { useEffect, useState } from 'react';
import { Archive, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { adminAPI } from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

/**
 * Archived Jobs page (Access Model V2).
 * Super-admin only. Lists soft-deleted jobs with an Unarchive action.
 */
const ArchivedJobs = () => {
  const { isSuperAdmin } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getArchivedJobs();
      setJobs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load archived jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUnarchive = async (job) => {
    if (!window.confirm(`Restore "${job.title || job.job_code}"? It will be visible in the active jobs list again.`)) return;
    setRestoringId(job.id);
    try {
      await adminAPI.unarchiveJob(job.id);
      toast.success('Job restored');
      setJobs(prev => prev.filter(j => j.id !== job.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore job');
    } finally {
      setRestoringId(null);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-6" data-testid="archived-jobs-no-access">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-3 max-w-lg">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-amber-900">Super-admin access required</h2>
            <p className="text-sm text-amber-800 mt-1">Only super-admins can view or restore archived jobs.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="archived-jobs-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <Archive className="w-5 h-5 text-slate-700" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Archived Jobs</h1>
          <p className="text-sm text-slate-500">Soft-deleted jobs. Restore any to bring them back to active.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="archived-jobs-empty">
            <Archive className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No archived jobs.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Job</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Archived on</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`archived-job-${j.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{j.title}</p>
                    <p className="text-xs text-slate-500">{j.job_code}</p>
                  </td>
                  <td className="p-3 text-slate-700">{j.client_name || '—'}</td>
                  <td className="p-3 text-slate-500 text-xs">
                    {j.deleted_at ? format(new Date(j.deleted_at), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => handleUnarchive(j)}
                      disabled={restoringId === j.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 text-xs font-medium disabled:opacity-60"
                      data-testid={`unarchive-${j.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {restoringId === j.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ArchivedJobs;
